import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { ArrowRight, ArrowLeft, ArrowRight as ArrowRightIcon, Check, BookOpen, Gavel, Palette } from "lucide-react";

// CDN images — re-used existing site imagery plus the new artist studio photos
// that were uploaded with this project.
const SLIDES = [
  {
    id: "discover",
    eyebrow: "A trusted marketplace · Est. 2026",
    icon: Palette,
    title: (
      <>
        Discover original<br />
        <span className="text-[#1E3A8A]">African art</span>{" "}
        <span className="italic font-light">— direct from the artist.</span>
      </>
    ),
    body: "A curated portal for collectors and contemporary masters working across the continent. Browse paintings, mixed media and sculpture from Harare, Johannesburg, Lusaka and beyond.",
    ctas: [
      { to: "/gallery", label: "Browse Art", primary: true, testid: "hero-cta-gallery" },
      { to: "/register", label: "Apply to Sell", testid: "hero-cta-sell" },
    ],
    checks: ["Original works only", "Direct from artists", "Secure checkout"],
    stat: { value: "120+", label: "Artists on the platform" },
    images: {
      large:
        "https://static.prod-images.emergentagent.com/jobs/2cf555d8-2b2e-4a65-8a76-f17be07c4f4a/images/3a6ccbd94dcf7c8dd2bb71e8eaac726f163f07a081c303340de9507a1d17e8f3.png",
      largeAlt: "Original African contemporary painting in bold reds and ochre tones",
      small1:
        "https://static.prod-images.emergentagent.com/jobs/2cf555d8-2b2e-4a65-8a76-f17be07c4f4a/images/17c8cb237e4f9c5b598a91469f15cccc2b2e147ca2cef00db3cd24c093ae891f.png",
      small1Alt: "Featured African contemporary portrait artwork on the marketplace",
      small2:
        "https://static.prod-images.emergentagent.com/jobs/2cf555d8-2b2e-4a65-8a76-f17be07c4f4a/images/d11b06ed5c6c920763ec229eb9d9f1c7ad885e95f977d086b28da211c4d8e0ef.png",
      small2Alt: "Portrait of a contemporary African artist at work in their studio",
    },
  },
  {
    id: "stories",
    eyebrow: "African Stories · long-form editorial",
    icon: BookOpen,
    title: (
      <>
        Read & collect<br />
        <span className="text-[#1E3A8A]">African Stories</span>{" "}
        <span className="italic font-light">— in their own words.</span>
      </>
    ),
    body: "Journalism, oral histories and studio reports from the people shaping contemporary African art — written by journalists and storytellers from the continent.",
    ctas: [
      { to: "/journal", label: "Read the journal", primary: true, testid: "hero-cta-journal" },
      { to: "/register", label: "Submit a story", testid: "hero-cta-storyteller" },
    ],
    checks: ["First-person voices", "Studio interviews", "New each week"],
    stat: { value: "Weekly", label: "New African Stories drops" },
    images: {
      large:
        "https://customer-assets.emergentagent.com/job_africa-art-masters/artifacts/2173ltfz_WhatsApp%20Image%202026-05-19%20at%2020.38.06.jpeg",
      largeAlt: "African Stories editorial cover photograph from the AfricaArtMasters journal",
      small1:
        "https://customer-assets.emergentagent.com/job_africa-art-masters/artifacts/67i7rp2h_Batsirai%20Muskwe.jpg",
      small1Alt: "Portrait of Zimbabwean artist Batsirai Muskwe featured in African Stories",
      small2:
        "https://customer-assets.emergentagent.com/job_africa-art-masters/artifacts/nhdka7vj_WhatsApp%20Image%202026-05-19%20at%2020.36.20.jpeg",
      small2Alt: "African Stories long-form editorial photograph of a contemporary artwork",
    },
  },
  {
    id: "auction",
    eyebrow: "Live auctions · real-time bidding",
    icon: Gavel,
    title: (
      <>
        Bid live at<br />
        <span className="text-[#1E3A8A]">auction</span>{" "}
        <span className="italic font-light">— compete from anywhere.</span>
      </>
    ),
    body: "Curated auction lots from the continent's contemporary masters. WebSocket-driven real-time bidding, transparent winning increments, secure escrow on every close.",
    ctas: [
      { to: "/auctions", label: "View live lots", primary: true, testid: "hero-cta-auctions" },
      { to: "/register", label: "Register to bid", testid: "hero-cta-bidder" },
    ],
    checks: ["Real-time bids", "Transparent increments", "Direct from studio"],
    stat: { value: "Live", label: "Lots closing this week" },
    images: {
      large:
        "https://customer-assets.emergentagent.com/job_africa-art-masters/artifacts/gf4fgltz_WhatsApp%20Image%202026-05-19%20at%2020.36.21%20%282%29.jpeg",
      largeAlt: "Featured auction lot — original African contemporary painting",
      small1:
        "https://customer-assets.emergentagent.com/job_africa-art-masters/artifacts/ry5mx7e0_WhatsApp%20Image%202026-05-19%20at%2020.36.20%20%281%29.jpeg",
      small1Alt: "Live African art auction lot — mixed media work",
      small2:
        "https://customer-assets.emergentagent.com/job_africa-art-masters/artifacts/13jmg64m_WhatsApp%20Image%202026-05-19%20at%2020.36.20%20%283%29.jpeg",
      small2Alt: "Live African art auction lot — contemporary painting close-up",
    },
  },
];

const AUTOPLAY_MS = 6000;

export const HomeHero = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  // Auto-advance with pause-on-hover. Clears + restarts the timer whenever
  // `active` or `paused` changes so manual clicks don't double-advance.
  useEffect(() => {
    if (paused) return undefined;
    timerRef.current = setTimeout(() => {
      setActive((a) => (a + 1) % SLIDES.length);
    }, AUTOPLAY_MS);
    return () => clearTimeout(timerRef.current);
  }, [active, paused]);

  const goTo = (idx) => setActive(((idx % SLIDES.length) + SLIDES.length) % SLIDES.length);
  const next = () => goTo(active + 1);
  const prev = () => goTo(active - 1);

  return (
    <section
      data-testid="home-hero"
      className="px-6 md:px-12 lg:px-24 pt-16 md:pt-20 pb-16 relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Slide track — translateX moves the whole strip horizontally.
          overflow-hidden lives INSIDE the section padding so neighbouring
          slides don't peek through the left/right whitespace.

          Mobile bug fix (v1.31): explicit `w-full flex-row flex-nowrap` on
          the track + `basis-full shrink-0 grow-0` on each child. Without
          these, iOS Safari occasionally treats `w-full` on a flex item as
          a hint rather than a constraint, letting two slides stack on top
          of each other when the column grid inside is taller than wide. */}
      <div className="overflow-hidden w-full">
        <div
          className="flex flex-row flex-nowrap w-full transition-transform duration-700 ease-[cubic-bezier(0.22,0.61,0.36,1)]"
          style={{ transform: `translateX(-${active * 100}%)` }}
          data-testid="hero-track"
        >
        {SLIDES.map((slide, slideIdx) => {
          const Icon = slide.icon;
          // SEO: only the first slide gets <h1>. Additional slides use <h2>
          // so the page has exactly one canonical H1 (Google best practice)
          // while still preserving the editorial heading hierarchy.
          const TitleTag = slideIdx === 0 ? "h1" : "h2";
          return (
            <div
              key={slide.id}
              data-testid={`hero-slide-${slide.id}`}
              className="basis-full shrink-0 grow-0 min-w-0 w-full"
            >
              {/* Single column on mobile: a 12-col grid's gap-8 alone (11 x 32px)
                  exceeds small phone widths and forces horizontal overflow. */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center">
                <div className="lg:col-span-6 reveal-up">
                  <div className="inline-flex items-center gap-2 border border-[#1E3A8A]/20 bg-[#F4C430]/15 px-3 py-1.5">
                    <Icon className="w-3.5 h-3.5 text-[#1E3A8A]" strokeWidth={1.5} />
                    <span className="label-eyebrow text-[#1E3A8A]">{slide.eyebrow}</span>
                  </div>
                  <TitleTag className="font-serif-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.02] mt-6 text-[#111]">
                    {slide.title}
                  </TitleTag>
                  <p className="mt-6 text-lg text-[#333] max-w-xl leading-relaxed">{slide.body}</p>
                  <div className="mt-8 flex flex-wrap gap-3">
                    {slide.ctas.map((cta) => (
                      <Link key={cta.to + cta.label} to={cta.to}>
                        <Button
                          data-testid={cta.testid}
                          variant={cta.primary ? "default" : "outline"}
                          className={
                            cta.primary
                              ? "rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white px-7 py-5 h-auto label-eyebrow"
                              : "rounded-none border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white px-7 py-5 h-auto label-eyebrow"
                          }
                        >
                          {cta.label}
                          {cta.primary && <ArrowRight className="ml-2 w-3.5 h-3.5" />}
                        </Button>
                      </Link>
                    ))}
                  </div>
                  <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#444]">
                    {slide.checks.map((t) => (
                      <span key={t} className="inline-flex items-center gap-1.5">
                        <Check className="w-4 h-4 text-[#1E3A8A]" /> {t}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="lg:col-span-6 reveal-up">
                  <div className="grid grid-cols-12 gap-4">
                    <div className="col-span-8 hover-zoom-wrap aspect-[4/5] bg-[#F2EDE5]">
                      <img
                        src={slide.images.large}
                        alt={slide.images.largeAlt || slide.eyebrow}
                        className="hover-zoom w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="col-span-4 flex flex-col gap-4">
                      <div className="hover-zoom-wrap aspect-square bg-[#F2EDE5]">
                        <img
                          src={slide.images.small1}
                          alt={slide.images.small1Alt || ""}
                          className="hover-zoom w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <div className="hover-zoom-wrap aspect-square bg-[#F2EDE5]">
                        <img
                          src={slide.images.small2}
                          alt={slide.images.small2Alt || ""}
                          className="hover-zoom w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <div className="bg-[#1E3A8A] text-white p-4">
                        <p className="font-mono-aam text-3xl leading-none">{slide.stat.value}</p>
                        <p className="label-eyebrow text-[#F4C430] mt-2">{slide.stat.label}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        </div>
      </div>

      {/* Controls — arrows + dot indicators */}
      <div className="mt-10 flex items-center justify-between gap-6">
        <div className="flex items-center gap-3" data-testid="hero-dots">
          {SLIDES.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => goTo(i)}
              data-testid={`hero-dot-${s.id}`}
              aria-label={`Show slide ${i + 1} — ${s.eyebrow}`}
              className={`group flex items-center gap-2 transition-colors`}
            >
              <span
                className={`block h-[2px] transition-all ${
                  active === i
                    ? "w-12 bg-[#1E3A8A]"
                    : "w-6 bg-[#E5E0D8] group-hover:bg-[#1E3A8A]/60"
                }`}
              />
              <span
                className={`label-eyebrow transition-colors ${
                  active === i ? "text-[#1E3A8A]" : "text-[#999]"
                }`}
              >
                0{i + 1}
              </span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={prev}
            data-testid="hero-prev"
            aria-label="Previous slide"
            className="w-11 h-11 border border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white transition-colors flex items-center justify-center"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={next}
            data-testid="hero-next"
            aria-label="Next slide"
            className="w-11 h-11 border border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white transition-colors flex items-center justify-center"
          >
            <ArrowRightIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

// Kept for backward compatibility with any importer expecting ARTIST_PORTRAIT.
export const ARTIST_PORTRAIT = SLIDES[0].images.small2;
